import { useStyles } from "./MainHeader.style";
import { getConfigGQL, meGQL } from "@onmo/graphql/graphql";
import { useQuery } from "@apollo/client";
import { User } from "../../../models/user";
import AppBar from "../AppBar/AppBar";
import { StaticImage } from "../StaticImage";
import Menu from "./assets/menu_1x.webp";
import Menu2x from "./assets/menu_2x.webp";
import Menu3x from "./assets/menu_3x.webp";
import Avatar from "../../core/Avatar/Avatar";
import {
  NavigateFunction,
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { PageRoutes } from "../../../constants/historyGaming";
import { ILevel } from "../../../types/user";
import { Icon } from "../../core/Icon/Icon";
import { useEffect, useMemo } from "react";
import { GamingLocation } from "../../../models/gamingLocation";
import { TabsList } from "../TabsList";
import { updateActiveTab as updateActiveTabGlobalLb } from "../../../store/slices/globalLeaderboards";
import { updateActiveTab as updateActiveTabShop } from "../../../store/slices/inventories";
import { LIST_TAB_RANK } from "../../../constants/rank";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { setIsShowService } from "../../../store/slices/popup";
import { Chip } from "../../core/Chip/Chip";
import { LIST_TAB_SHOP } from "../../../constants/shop";
import {
  selectCurrentFriendTabTitle,
  selectFriendTabListWithCount,
  updateFriendActiveTab,
} from "../../../store/slices/friends";
import { HistoryGaming } from "../../../models/historyGaming";
import { QueryParams } from "../../../models/queryParam";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { assetsImages } from "../../../models/theme";
import { CurrencyIcon } from "../../../constants/wallet";
import { useWalletConfig } from "../../../hooks/useWalletConfig";
import { FEATURES } from "../../../types/theme";

const headerConfig = (
  page: "home" | "rank" | "shop" | "profile" | "friends" | "bundleDetail",
  avatar: string,
  t: TFunction<"translation", undefined>,
  classes: Record<string, string>,
  navigate: NavigateFunction,
  onShowService?: () => void,
  userId?: string,
  spendableGzTokens?: number,
  userRank?: ILevel,
  spendableIcon?: CurrencyIcon,
  hasShopTab?: boolean,
  hideProfile?: boolean,
  hideSetting?: boolean,
  isFromReward?: boolean,
) => {
  const handleNavigate = (route: string) => () => navigate(route);

  const renderLeftItems = () => {
    switch (page) {
      case "shop":
      case "bundleDetail":
      case "profile":
      case "friends":
        if (page !== "shop" || hasShopTab) {
          return (
            <Icon
              name="icon-bold-arrow-left-3"
              className={classes.icon}
              onClick={() => {
                if (isFromReward) {
                  navigate(PageRoutes.HomePage, { replace: true });
                } else {
                  HistoryGaming.back(navigate);
                }
              }}
              data-testid="back-icon"
            />
          );
        }
      default:
        return (
          <StaticImage
            src={assetsImages.headerLogo}
            onClick={handleNavigate(PageRoutes.HomePage)}
            data-testid={`logo-${page}`}
            className={classes.logo}
          />
        );
    }
  };

  const renderCenterItems = () => {
    switch (page) {
      case "profile":
        return (
          <Typography
            variant={TypographyVariant.BodyB4Medium}
            data-testid="profile-title"
          >
            {t(userId ? "common_Profile" : "common_My Profile")}
          </Typography>
        );
      case "bundleDetail":
      case "shop":
        return (
          <Avatar
            url={avatar}
            alt="avatar-user"
            onClick={handleNavigate(PageRoutes.Profile)}
            data-testid={`avatar-${page}`}
          />
        );
      case "friends":
        return (
          <Typography
            variant={TypographyVariant.BodyB4Medium}
            data-testid="friends-title"
          >
            {t("common_Friends")}
          </Typography>
        );
      default:
        return null;
    }
  };

  const renderRightItems = () => {
    const hasUnread = localStorage.getItem("has_unread") === "1";
    switch (page) {
      case "home":
      case "rank":
        return (
          <>
            {!hideProfile && (
              <Avatar
                url={avatar}
                userRank={userRank}
                alt="avatar-user"
                onClick={handleNavigate(PageRoutes.Profile)}
                data-testid={`avatar-${page}`}
              />
            )}
            {/* <Icon
              name="icon-linear-notification"
              className={classes.icon}
              onClick={handleNavigate(PageRoutes.Notifications)}
              data-testid="notification-icon"
            /> */}
            <StaticImage
              src={Menu}
              src2x={Menu2x}
              src3x={Menu3x}
              onClick={onShowService}
              data-testid="menu-icon"
            />
            {!hideSetting && (
              <Icon
                name="icon-bold-setting-2"
                className={classes.icon}
                onClick={handleNavigate(PageRoutes.Settings)}
                data-testid="settings-icon"
              />
            )}
          </>
        );
      case "bundleDetail":
      case "shop":
        return (
          <div className={classes.balance}>
            <Typography
              variant={TypographyVariant.BodyB6Regular}
              data-testid="balance-text"
            >
              {t("shop_Balance")}
            </Typography>
            <div className={classes.spendableGems}>
              <Chip coinIcon={spendableIcon} size={18} />
              <Typography variant={TypographyVariant.BodyB6Bold}>
                {spendableGzTokens ?? 0}
              </Typography>
            </div>
          </div>
        );
      case "profile": {
        return (
          !userId && (
            <>
              <div
                className={classes.noti}
                onClick={() => {
                  localStorage.setItem("has_unread", "0");
                  handleNavigate(PageRoutes.Notifications)();
                }}
              >
                <Icon
                  className={classes.icon}
                  name="icon-bold-notification"
                  data-testid="shop-icon"
                />
                {hasUnread && <span className={classes.redDot} />}
              </div>
              <Icon
                className={classes.icon}
                name="icon-bold-setting-2"
                onClick={handleNavigate(PageRoutes.Settings)}
                data-testid="settings-icon"
              />
            </>
          )
        );
      }
      case "friends": {
        return (
          <Icon
            className={classes.icon}
            name="icon-bold-search-normal"
            onClick={handleNavigate(PageRoutes.Search)}
            data-testid="friend-search-icon"
          />
        );
      }
      default:
        return null;
    }
  };

  return {
    leftItems: renderLeftItems(),
    centerItems: renderCenterItems(),
    rightItems: renderRightItems(),
  };
};

const MainHeader: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { fromReward?: boolean } | null;
  const isFromReward = !!locationState?.fromReward;
  const { t } = useTranslation();
  const { walletConfig, spendableIcon } = useWalletConfig();
  const baseCoinType = walletConfig.primaryCurrency.base;
  const dispatch = useAppDispatch();
  const { userId, bundleId } = useParams<{
    userId: string;
    bundleId: string;
  }>();

  const { data: dataMe } = useQuery(meGQL);
  const { data: dataConfig } = useQuery(getConfigGQL);
  const friendTabListWithCount = useAppSelector(selectFriendTabListWithCount);
  const activeFriendTab = useAppSelector(selectCurrentFriendTabTitle);

  const { activeTab: activeTabGlobalLb } = useAppSelector(
    (state) => state.globalLeaderboards,
  );
  const { activeTab: activeTabShop } = useAppSelector(
    (state) => state.inventories,
  );

  const { theme } = useAppSelector((state) => state.theme);
  const hideProfile = theme?.hideFeature?.includes(FEATURES.headerProfile);
  const hideSetting = theme?.hideFeature?.includes(FEATURES.headerSetting);

  const spendableGzTokens = User.getSpendableGzTokens(
    dataMe?.me?.coinWallets,
    walletConfig.primaryCurrency.spendable,
  );
  const userRank = User.getCurrentRank({
    wallet: dataMe?.me?.coinWallets,
    tierLevels: dataConfig?.config?.tierLevels,
    baseCoinType,
    xp: dataMe?.me?.xp,
  });
  const pathname = window.location.pathname;
  const page = GamingLocation.getCurrentLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const hasShopTab = pathname === PageRoutes.Shop && !!tab;

  const onChangeFriendTab = (tab: string) => {
    dispatch(updateFriendActiveTab({ tab }));
    window.scrollTo(0, 0);
    navigate(
      {
        pathname: PageRoutes.Friends,
        search: `friendTab=${tab.split(" ")[0]}`,
      },
      {
        replace: true,
      },
    );
  };

  const onChangeRankTab = (tab: string) => {
    window.scrollTo(0, 0);
    dispatch(updateActiveTabGlobalLb({ activeTab: tab }));
  };
  const onChangeShopTab = (tab: string) => {
    window.scrollTo(0, 0);
    dispatch(updateActiveTabShop({ activeTab: tab }));
  };

  const header = useMemo(() => {
    const onShowService = () => {
      dispatch(setIsShowService({ isShowService: true }));
    };
    return headerConfig(
      page,
      dataMe?.me.avatar,
      t,
      classes,
      navigate,
      onShowService,
      userId,
      spendableGzTokens,
      userRank,
      spendableIcon,
      hasShopTab,
      hideProfile,
      hideSetting,
      isFromReward,
    );
  }, [
    page,
    dataMe?.me.avatar,
    t,
    classes,
    navigate,
    userId,
    spendableGzTokens,
    userRank,
    dispatch,
    spendableIcon,
    hasShopTab,
    hideProfile,
    hideSetting,
    isFromReward,
  ]);

  useEffect(() => {
    if (QueryParams.getFriendTab()) {
      dispatch(updateFriendActiveTab({ tab: QueryParams.getFriendTab() }));
    } else dispatch(updateFriendActiveTab({ tab: friendTabListWithCount[0] }));
  }, [dispatch, friendTabListWithCount]);

  const renderTabContent = () => {
    switch (window.location.pathname) {
      case PageRoutes.Rank:
        return (
          <TabsList
            activeTab={activeTabGlobalLb}
            onChangeTab={onChangeRankTab}
            listTabs={LIST_TAB_RANK}
            className={classes.tabs}
          />
        );

      case PageRoutes.Friends:
        return (
          <TabsList
            activeTab={activeFriendTab}
            onChangeTab={onChangeFriendTab}
            listTabs={friendTabListWithCount}
            className={classes.tabs}
          />
        );
      case PageRoutes.Shop:
        return bundleId ? null : (
          <TabsList
            activeTab={activeTabShop}
            onChangeTab={onChangeShopTab}
            listTabs={LIST_TAB_SHOP}
            className={classes.tabs}
          />
        );
    }
  };
  return (
    <AppBar
      leftItems={header.leftItems}
      centerItems={header.centerItems}
      rightItems={header.rightItems}
      tabs={renderTabContent()}
    />
  );
};

export default MainHeader;
